import {VAT} from '@crm/utils/constants';
import ExtraServicesPriceLabel from './price-label';
import {DiscountCode, Frequency, PaymentInterval, Service} from '@crm/api/models/common/housekeepr-api';
import {ExtraServiceRequest} from '@crm/api/models/requests/housekeepr-api';
import {housekeeprApi} from '@crm/api/housekeepr-api';

const ExtraServicesPriceListByFreq = ({
    basePrice,
    craftsmanDeduction,
    paymentInterval,
    service,
    extraServices,
    frequency,
    discountCode,
}: {
    basePrice: number;
    craftsmanDeduction: number;
    paymentInterval: PaymentInterval | undefined;
    service: Service;
    extraServices: ExtraServiceRequest[];
    frequency: Frequency;
    discountCode?: DiscountCode;
}) => {
    const {data: availableExtraServices} = housekeeprApi.useGetExtraServicesQuery(service.id);

    const priceWithVAT = basePrice * VAT;
    const extraServicesPrice = availableExtraServices
        ? extraServices
              ?.filter((value) => value.count > 0)
              .sort((extraService1, extraService2) => (extraService1.how_often ?? 0) - (extraService2.how_often ?? 0))
              .map((orderExtraService) => {
                  const extraService = availableExtraServices.find(
                      (extraService) => extraService.id == orderExtraService.id
                  );
                  if (!extraService) {
                      return 0;
                  }
                  return extraService.price * (orderExtraService.count ?? 1) * VAT;
              })
              .reduce((sum, price) => sum + price, 0)
        : 0;

    const discountCodeDiscount = discountCode
        ? (priceWithVAT * (discountCode.percent_off ?? 0)) / 100 + (discountCode.value ?? 0) * VAT
        : 0;

    const paymentIntervalDiscount = paymentInterval
        ? ((priceWithVAT - discountCodeDiscount + extraServicesPrice) * (paymentInterval.discount ?? 0)) / 100
        : 0;

    const sum = priceWithVAT + extraServicesPrice - discountCodeDiscount - paymentIntervalDiscount;

    const formatOptions: Intl.NumberFormatOptions = {
        style: 'currency',
        currency: 'DKK',
    };

    return (
        <div className='ml-4'>
            <div key={`service-${service.id}`} className='flex justify-between py-0'>
                <ExtraServicesPriceLabel style={{paddingRight: 15}}>{service.name}</ExtraServicesPriceLabel>
                <ExtraServicesPriceLabel>{priceWithVAT.toLocaleString('da-DK', formatOptions)}</ExtraServicesPriceLabel>
            </div>
            {availableExtraServices &&
                extraServices
                    .filter((value) => value.count > 0)
                    .sort(
                        (extraService1, extraService2) =>
                            (extraService1.how_often ?? 0) - (extraService2.how_often ?? 0)
                    )
                    .map((orderExtraService) => {
                        const extraService = availableExtraServices.find(
                            (extraService) => extraService.id == orderExtraService.id
                        );
                        if (!extraService) {
                            return <></>;
                        }
                        const price = extraService.price * (orderExtraService.count ?? 1) * VAT;
                        const weekFrequency = frequency.every * orderExtraService.how_often;
                        const weekFrequencyString = weekFrequency == 1 ? 'hver uge' : `hver ${weekFrequency}. uge`;
                        return (
                            <div key={orderExtraService.id} className='flex justify-between py-0'>
                                <ExtraServicesPriceLabel style={{paddingRight: 15}}>
                                    {weekFrequency == 0
                                        ? `${extraService.unit == 'm²' ? '' : `${orderExtraService.count} × `}${
                                              extraService.name
                                          }`
                                        : `${extraService.unit == 'm²' ? '' : `${orderExtraService.count} × `}${
                                              extraService.name
                                          } (${weekFrequencyString})`}
                                </ExtraServicesPriceLabel>
                                <ExtraServicesPriceLabel>
                                    {price.toLocaleString('da-DK', formatOptions)}
                                </ExtraServicesPriceLabel>
                            </div>
                        );
                    })}
            {discountCode && (
                <div className='flex justify-between pt-2'>
                    <ExtraServicesPriceLabel style={{paddingRight: 15}}>
                        Rabatkode (
                        {discountCode.type === 'percentage'
                            ? `${discountCode.percent_off}% rabat`
                            : `${discountCodeDiscount} kr. rabat`}
                        )
                    </ExtraServicesPriceLabel>
                    <ExtraServicesPriceLabel>
                        -{discountCodeDiscount.toLocaleString('da-DK', formatOptions)}
                    </ExtraServicesPriceLabel>
                </div>
            )}
            {paymentInterval && paymentInterval.discount > 0 && (
                <div className='flex justify-between pt-2'>
                    <ExtraServicesPriceLabel style={{paddingRight: 15}}>
                        Betalingsinterval på{' '}
                        {paymentInterval.months > 1
                            ? `${paymentInterval.months} måneder`
                            : `${paymentInterval.months} måneder`}{' '}
                        ({paymentInterval.discount}% rabat)
                    </ExtraServicesPriceLabel>
                    <ExtraServicesPriceLabel>
                        -{paymentIntervalDiscount.toLocaleString('da-DK', formatOptions)}
                    </ExtraServicesPriceLabel>
                </div>
            )}
            <div className='flex justify-between pt-2'>
                <ExtraServicesPriceLabel style={{paddingRight: 15}}>Total</ExtraServicesPriceLabel>
                <ExtraServicesPriceLabel style={{textDecoration: 'underline'}}>
                    {sum.toLocaleString('da-DK', formatOptions)}
                </ExtraServicesPriceLabel>
            </div>
            <div className='flex justify-between pb-2'>
                <ExtraServicesPriceLabel style={{paddingRight: 15, opacity: 0.5}}>
                    Efter servicefradrag (ca. {craftsmanDeduction}%)
                </ExtraServicesPriceLabel>
                <ExtraServicesPriceLabel style={{opacity: 0.5}}>
                    {(sum * (1 - craftsmanDeduction / 100)).toLocaleString('da-DK', formatOptions)}
                </ExtraServicesPriceLabel>
            </div>
        </div>
    );
};

export default ExtraServicesPriceListByFreq;
